import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useBodyScrollLock } from './useBodyScrollLock';
import { useModalStack } from './useModalStack';
import type {
  ModalAccent,
  ModalCloseReason,
  ModalIconTone,
  ModalMobilePresentation,
  ModalSize,
  ModalVariant,
} from './modal.types';

export interface ModalShellProps {
  /** Kontrollierter Offen-Zustand. */
  open: boolean;
  /** Schließen-Callback mit definiertem Grund. */
  onClose: (reason: ModalCloseReason) => void;
  variant: ModalVariant;
  size?: ModalSize;
  title?: React.ReactNode;
  /** Kurze Unterzeile/ID/Status im Header. */
  subtitle?: React.ReactNode;
  /** Kleines Outline-Icon links im Header. */
  icon?: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
  /** Optionales rundes Profil-/Avatarbild links im Header (statt `icon`). Bei Ladefehler Fallback auf `avatarInitials`, sonst `icon`. */
  avatarUrl?: string;
  /** Initialen als runder Avatar-Fallback (z.B. "AB"), wenn kein/fehlerhaftes Bild. */
  avatarInitials?: string;
  /** Eigenes Kopf-Element, z.B. ein per Tastatur bedienbarer Profilbild-Button. */
  headerVisual?: React.ReactNode;
  /** Semantischer Akzent. Bestimmt Standard-Icon-Ton + alertdialog-Rolle. */
  accent?: ModalAccent;
  /** Farbton des Header-Icons. Standard aus `accent` abgeleitet. */
  iconTone?: ModalIconTone;
  /** Mobile Darstellung. Standard: viewer=fullscreen, confirm=centered, sonst sheet. */
  mobilePresentation?: ModalMobilePresentation;
  /** Backdrop-Klick schließt. Standard: true. */
  closeOnBackdrop?: boolean;
  /** Escape schließt. Standard: true. */
  closeOnEscape?: boolean;
  /** Schließen-Button (X) im Header ausblenden (z.B. erzwungene Auswahl). */
  hideCloseButton?: boolean;
  /** Zusätzliche Aktionen rechts im Header, direkt vor dem Schließen-Button. */
  headerActions?: React.ReactNode;
  /** Zeigt bei mobilen Bottom-Sheets den visuellen Griff an. */
  showMobileGrabber?: boolean;
  /** Sticky-Footer-Inhalt (z.B. <ModalActions/>). */
  footer?: React.ReactNode;
  /** Optionale aria-describedby-Referenz. */
  describedById?: string;
  /** Element, das beim Öffnen fokussiert wird (sonst erstes fokussierbares). */
  initialFocusRef?: React.RefObject<HTMLElement>;
  children?: React.ReactNode;
  /** Zusätzliche Klassen für den Body-Bereich. */
  bodyClassName?: string;
}

// Claude-Design-Entwurf „Einheitliches Fenster-Design": 420/560/720/920.
const SIZE_MAX: Record<ModalSize, string> = {
  sm: 'sm:max-w-[420px]',
  md: 'sm:max-w-[560px]',
  lg: 'sm:max-w-[720px]',
  xl: 'sm:max-w-[920px]',
};

// Getöntes Header-Icon (soft-BG + Textfarbe), Dark-Mode ergänzt (Entwurf ist light-only).
const ICON_TONE: Record<ModalIconTone, string> = {
  accent: 'bg-primary-50 text-primary-600 dark:bg-primary-900/25 dark:text-primary-400',
  danger: 'bg-error-50 text-error-600 dark:bg-error-900/25 dark:text-error-400',
  warning: 'bg-warning-50 text-warning-600 dark:bg-warning-900/25 dark:text-warning-400',
  info: 'bg-info-50 text-info-600 dark:bg-info-900/25 dark:text-info-400',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function defaultMobilePresentation(variant: ModalVariant): ModalMobilePresentation {
  if (variant === 'viewer') return 'fullscreen';
  if (variant === 'confirm' || variant === 'progress') return 'centered';
  return 'sheet';
}

/**
 * Zentraler Modal-Rahmen (Issue #11) im Claude-Design-Entwurf
 * „Einheitliches Fenster-Design": ruhiger dunkler Backdrop mit leichtem Blur,
 * neutraler Header mit getöntem Icon (ohne Akzentlinie), sticky Footer auf
 * gesenkter Fläche, kompakte Radien.
 *
 * Ergänzt gemäß unseren Projekt-Richtlinien: Dark-Mode, Borders, keine
 * Gradienten, Mobile-First (Bottom-Sheet), 44px-Touch-Ziele, Fokusfalle,
 * Body-Scroll-Lock, Z-Index-Stack, A11y, prefers-reduced-motion.
 */
export const ModalShell: React.FC<ModalShellProps> = ({
  open,
  onClose,
  variant,
  size = 'md',
  title,
  subtitle,
  icon: Icon,
  avatarUrl,
  avatarInitials,
  headerVisual,
  accent = 'brand',
  iconTone,
  mobilePresentation,
  closeOnBackdrop = true,
  closeOnEscape = true,
  hideCloseButton = false,
  headerActions,
  showMobileGrabber = false,
  footer,
  describedById,
  initialFocusRef,
  children,
  bodyClassName = '',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<Element | null>(null);
  const [mounted, setMounted] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const titleId = useId();

  // Avatar-Ladefehler zurücksetzen, wenn sich die Bild-URL ändert.
  useEffect(() => {
    setAvatarError(false);
  }, [avatarUrl]);

  const { zIndex } = useModalStack(open);
  useBodyScrollLock(open);

  const tone: ModalIconTone = iconTone ?? (accent === 'danger' ? 'danger' : 'accent');
  const isAlert = variant === 'confirm' && (accent === 'danger' || tone === 'danger');
  const mobile = mobilePresentation ?? defaultMobilePresentation(variant);
  const isViewer = variant === 'viewer';
  const showAvatarImg = Boolean(avatarUrl) && !avatarError;
  const showAvatarInitials = !showAvatarImg && Boolean(avatarInitials);
  const hasHeaderVisual = Boolean(headerVisual) || showAvatarImg || showAvatarInitials || Boolean(Icon);

  // Enter-Animation (respektiert prefers-reduced-motion via CSS-Klassen).
  useEffect(() => {
    if (!open) {
      setMounted(false);
      return;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Fokus setzen beim Öffnen, Rückgabe beim Schließen.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement;
    const focusTarget =
      initialFocusRef?.current ||
      panelRef.current?.querySelector<HTMLElement>(FOCUSABLE) ||
      panelRef.current;
    const t = window.setTimeout(() => focusTarget?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      const prev = previouslyFocused.current as HTMLElement | null;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, initialFocusRef]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        e.stopPropagation();
        onClose('escape');
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [closeOnEscape, onClose],
  );

  if (!open || typeof document === 'undefined') return null;

  const overlayAlign =
    mobile === 'sheet'
      ? 'items-end sm:items-center justify-center'
      : mobile === 'fullscreen'
        ? 'items-stretch justify-center sm:items-center'
        : 'items-center justify-center';

  const overlayPadding = mobile === 'sheet' ? 'p-0 sm:p-6' : 'p-0 sm:p-6';

  // Radius/Breite je Mobile-Variante (Desktop 16px, Bottom-Sheet oben 22px).
  const panelShape =
    mobile === 'sheet'
      ? 'w-full max-w-full rounded-t-[22px] sm:rounded-2xl max-h-[94dvh] sm:max-h-[90vh]'
      : mobile === 'fullscreen'
        ? 'h-full w-full max-w-full rounded-none sm:h-auto sm:rounded-2xl sm:max-h-[90vh]'
        : 'mx-4 w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] rounded-2xl max-h-[90vh] sm:mx-0';

  const surface = isViewer
    ? 'bg-gray-900 text-gray-100 border-gray-800'
    : 'bg-white text-gray-900 dark:bg-gray-800 dark:text-white border-gray-200 dark:border-gray-700';

  const enterState = mounted
    ? 'opacity-100 translate-y-0 scale-100'
    : 'opacity-0 translate-y-2 scale-[0.98]';

  const overlay = (
    <div
      className={`fixed inset-0 flex ${overlayAlign} ${overlayPadding}`}
      style={{ zIndex }}
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop: ruhiges dunkles Slate (rgba(20,26,32,.44)) + leichter Blur */}
      <div
        className={`absolute inset-0 backdrop-blur-[2px] transition-opacity duration-200 motion-reduce:transition-none ${
          mounted ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundColor: 'rgba(20,26,32,0.44)' }}
        aria-hidden="true"
        onClick={closeOnBackdrop ? () => onClose('backdrop') : undefined}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role={isAlert ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={describedById}
        className={`relative flex min-w-0 flex-col overflow-hidden border shadow-[0_18px_48px_rgba(20,26,32,0.16),0_6px_14px_rgba(20,26,32,0.08)] dark:shadow-[0_18px_48px_rgba(0,0,0,0.5)] ${surface} ${SIZE_MAX[size]} ${panelShape} transition-all duration-[240ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${enterState}`}
      >
        {mobile === 'sheet' && showMobileGrabber && (
          <span
            className="pointer-events-none absolute left-1/2 top-2 z-10 h-1 w-10 -translate-x-1/2 rounded-full bg-gray-300 dark:bg-gray-600 sm:hidden"
            aria-hidden="true"
          />
        )}

        {/* Header (neutral, getöntes Icon/Avatar, keine Akzentlinie) */}
        {(title || subtitle || hasHeaderVisual) && (
          <div
            className={`flex min-w-0 flex-shrink-0 items-start gap-3 border-b px-4 py-4 sm:px-5 sm:py-[18px] ${
              isViewer ? 'border-gray-800' : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            {headerVisual ? (
              <div className="flex-shrink-0">{headerVisual}</div>
            ) : showAvatarImg ? (
              <img
                src={avatarUrl}
                alt=""
                aria-hidden="true"
                onError={() => setAvatarError(true)}
                className="h-[38px] w-[38px] flex-shrink-0 rounded-full border border-gray-200 object-cover dark:border-gray-700"
              />
            ) : showAvatarInitials ? (
              <span
                className={`flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${ICON_TONE[tone]}`}
                aria-hidden="true"
              >
                {avatarInitials}
              </span>
            ) : Icon ? (
              <span
                className={`flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-lg ${ICON_TONE[tone]}`}
              >
                <Icon className="h-[19px] w-[19px]" aria-hidden="true" />
              </span>
            ) : null}
            <div className={`min-w-0 flex-1 ${hasHeaderVisual ? 'pt-0.5' : ''}`}>
              {title && (
                <h2
                  id={titleId}
                  className="line-clamp-2 break-words text-lg font-bold leading-snug tracking-tight"
                >
                  {title}
                </h2>
              )}
              {subtitle && (
                <p
                  className={`break-words text-sm sm:truncate ${isViewer ? 'text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  {subtitle}
                </p>
              )}
            </div>
            {headerActions}
            {!hideCloseButton && (
              <button
                type="button"
                onClick={() => onClose('button')}
                aria-label="Schließen"
                className={`app-modal-close -mr-1.5 -mt-1 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
                  isViewer
                    ? 'text-gray-300 hover:!bg-white/10'
                    : 'text-gray-500 hover:!bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:text-gray-400 dark:hover:!bg-gray-700 dark:hover:!text-white dark:focus-visible:ring-primary-400/50'
                }`}
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {/* Body – nur dieser Bereich scrollt */}
        <div
          className={`min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto ${
            isViewer ? 'bg-gray-900 p-0' : 'p-4 sm:p-5'
          } ${bodyClassName}`}
        >
          {children}
        </div>

        {/* Footer – sticky, gesenkte Fläche */}
        {footer && (
          <div
            className={`min-w-0 flex-shrink-0 border-t px-4 py-3.5 sm:px-5 ${
              isViewer
                ? 'border-gray-800 bg-gray-900'
                : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40'
            }`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default ModalShell;
